import { RouteComponentProps, useMatch } from '@reach/router';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import {
  AccountBalanceContainer,
  Box,
  CheckCreditsContainer,
  CheckCreditsItem,
  DateContainer,
  IClientItem,
  IClientStats,
  IHistory,
  LineBadge,
  TextColor,
} from '../../SmsPanel/SmsPanel';
import {
  endOfDate,
  jMonthIndex,
  monthToTimestamp,
  smartDateString,
  startOfDate,
} from '../../../utils/date';
import moment from 'moment-jalaali';
import { getDomainAndPort } from '../../../api/Utils';
import axios from 'axios';
import styled from 'styled-components';
import ButtonGroup from '../../../components/ButtonGroup';
import { MultiDatePicker_Range } from '../../../components/MyDatePicker/MultiDatePicker';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import Input from '../../../components/Input/Input';
import Button from '../../../components/Button/Button';
import notify from '../../../components/Notify';
import { black, green, red } from '../../../utils/style/colors';

interface IAdminSmsPanelProps {}

type ComponentProps = IAdminSmsPanelProps & RouteComponentProps;

const DOMAIN = getDomainAndPort().protocolDomain;

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  color: ${black};
`;

const AdminSmsPanel: React.FC<ComponentProps> = () => {
  const { t } = useTranslation('general');

  const dateOptions = {
    currentMonth: `${t('currentMonth')}`,
    prevMonth: `${t('prevMonth')}`,
    currentSeason: `${t('currentSeason')}`,
    prevSeason: `${t('prevSeason')}`,
    custom: `${t('customDateSetting')}`,
  };

  const matcher = useMatch('/t/:companyId/admin/sms-panel');
  const companyId = matcher ? matcher.companyId : null;

  const thisUser = useSelector((state: RootState) => state.thisUser);
  const users = useSelector((state: RootState) => state.users.map);

  const [currentCompanySmsInfo, setCurrentCompanySmsInfo] =
    useState<IClientItem | null>(null);
  const [selectedDateOption, setSelectedDateOptions] = useState<
    keyof typeof dateOptions | ''
  >('');
  const [notificationLine, setNotificationLine] = useState<string | null>(null);
  const [selectedUserNotificationLine, setSelectedUserNotificationLine] =
    useState<string | null>(null);
  const [customDate, setCustomDate] = useState<{
    startDate: number;
    dueDate: number;
  }>();
  const [clientStats, setClientStats] = useState<IClientStats[]>();
  const [history, setHistory] = useState<IHistory[]>();
  const [loading, setLoading] = useState<boolean>(false);

  const validatePhoneNumber = () => {
    return notificationLine
      ? /^(\+98|0098|098|0)?9\d{9}$/.test(notificationLine)
      : false;
  };

  const getStandardPhoneNumber = (phone: string) => {
    const match = phone.match(/(\d{9})$/);
    return match ? `09${match[1]}` : '';
  };

  const handleGetSmsInfo = async () => {
    const res = await axios.get(`${DOMAIN}/man/rest/company/smsInfo`, {
      headers: { Authorization: thisUser.access_token },
      params: { companyId },
    });
    setCurrentCompanySmsInfo(res.data);
  };

  const handleGetStatistics = async () => {
    const res = await axios.get(`${DOMAIN}/man/rest/company/smsStats`, {
      params: {
        companyId: companyId,
        startDate: customDate?.startDate,
        endDate: customDate?.dueDate,
      },
      headers: {
        Authorization: thisUser.access_token,
      },
    });

    setClientStats(res.data);
  };

  const handleGetHistory = async () => {
    const res = await axios.get(
      `${DOMAIN}/man/rest/company/smsAddCreditHistory`,
      {
        params: { companyId },
        headers: {
          Authorization: thisUser.access_token,
        },
      }
    );

    setHistory(res.data);
  };

  const handleSaveUserInfo = async () => {
    setLoading(true);
    const isValidPhoneNumber = validatePhoneNumber();
    if (!isValidPhoneNumber) {
      notify(t('invalidPhoneNumber'), 'error');
      setLoading(false);
      return;
    }
    try {
      const res = await axios.post(
        `${DOMAIN}/man/rest/company/updateNotificationLine`,
        {},
        {
          params: {
            companyId,
            phoneNumber: getStandardPhoneNumber(notificationLine!),
          },
          headers: {
            Authorization: thisUser.access_token,
          },
        }
      );
      setCurrentCompanySmsInfo(res.data);
      notify(t('savedSuccessfully'));
    } catch (e) {
      notify(t('savedFailed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      handleGetSmsInfo();
      handleGetHistory();
      handleGetStatistics();
    }
  }, [companyId]);

  useEffect(() => {
    const currentDate = new Date();
    const currentMonthIndex = jMonthIndex(
      Number(moment(currentDate).format('jYYYY')),
      Number(moment(currentDate).format('jM'))
    );

    if (selectedDateOption !== 'custom') {
      setCustomDate({ startDate: 0, dueDate: 0 });
    }

    if (selectedDateOption === 'currentMonth' || selectedDateOption === '') {
      const currentMonthTimestamp = new Date(
        monthToTimestamp(currentMonthIndex)
      );
      const startOfCurrentMonth = startOfDate(
        currentMonthTimestamp,
        'month'
      ).getTime();
      const endOfCurrentMonth = endOfDate(
        currentMonthTimestamp,
        'month'
      ).getTime();

      setCustomDate({
        startDate: startOfCurrentMonth,
        dueDate: endOfCurrentMonth,
      });
    } else if (selectedDateOption === 'prevMonth') {
      const prevMonthTimestamp = new Date(
        monthToTimestamp(currentMonthIndex - 1)
      );
      const startOfPrevMonth = startOfDate(
        prevMonthTimestamp,
        'month'
      ).getTime();
      const endOfPrevMonth = endOfDate(prevMonthTimestamp, 'month').getTime();
      setCustomDate({
        startDate: startOfPrevMonth,
        dueDate: endOfPrevMonth,
      });
    } else if (selectedDateOption === 'currentSeason') {
      const currentMonthTimestamp = new Date(
        monthToTimestamp(currentMonthIndex)
      );
      const startOfCurrentSeason = startOfDate(
        currentMonthTimestamp,
        'season'
      ).getTime();
      const endOfCurrentSeason = endOfDate(
        currentMonthTimestamp,
        'season'
      ).getTime();
      setCustomDate({
        startDate: startOfCurrentSeason,
        dueDate: endOfCurrentSeason,
      });
    } else if (selectedDateOption === 'prevSeason') {
      const prevMonthSeasonTimestamp = new Date(
        monthToTimestamp(currentMonthIndex - 3)
      );
      const startOfPrevSeason = startOfDate(
        prevMonthSeasonTimestamp,
        'season'
      ).getTime();
      const endOfPrevSeason = endOfDate(
        prevMonthSeasonTimestamp,
        'season'
      ).getTime();
      setCustomDate({
        startDate: startOfPrevSeason,
        dueDate: endOfPrevSeason,
      });
    }
  }, [selectedDateOption]);

  useEffect(() => {
    if (
      currentCompanySmsInfo &&
      (customDate?.startDate || customDate?.dueDate)
    ) {
      // handleGetSummary();
      handleGetStatistics();
      handleGetHistory();
    }
  }, [customDate]);

  useEffect(() => {
    if (currentCompanySmsInfo) {
      setNotificationLine(currentCompanySmsInfo.notificationLine);
    }
  }, [currentCompanySmsInfo]);

  useEffect(() => {
    if (notificationLine && notificationLine.length > 10) {
      const relatedUserBaseonNotificationLine = Object.values(users).find(
        (user) => user.phoneNumber === getStandardPhoneNumber(notificationLine)
      );

      if (relatedUserBaseonNotificationLine) {
        setSelectedUserNotificationLine(relatedUserBaseonNotificationLine.name);
      } else {
        setSelectedUserNotificationLine(t('noUserMatch'));
      }
    }
  }, [notificationLine]);

  return (
    <Container>
      <AccountBalanceContainer>
        <LineBadge>
          <div className="font-bold">{t('currentBalance')} : </div>
          <div className="flex gap-2 items-center">
            <TextColor
              color={
                (currentCompanySmsInfo?.balanceIRR || 0) >= 0 ? green : red
              }
            >
              {currentCompanySmsInfo?.balanceIRR.toLocaleString()}
            </TextColor>
            <span>{t('IRR') as string}</span>
          </div>
        </LineBadge>
      </AccountBalanceContainer>
      <Box>
        <CheckCreditsContainer>
          <CheckCreditsItem>
            {/*<Input*/}
            {/*    id={`checkCredits-${currentCompanySmsInfo?.id}`}*/}
            {/*    type="checkbox"*/}
            {/*    checked={currentCompanySmsInfo?.balanceCheck}*/}
            {/*    label={t('checkCredits')}*/}
            {/*    disabled={true}*/}
            {/*/>*/}
            {/*<Input*/}
            {/*    type="number"*/}
            {/*    id="credit"*/}
            {/*    label={t('minCredit')}*/}
            {/*    wrapperStyle={{ flex: 1 }}*/}
            {/*    value={currentCompanySmsInfo?.allowBalance}*/}
            {/*    style={{ textAlign: 'start' }}*/}
            {/*    disabled={true}*/}
            {/*/>*/}
            {currentCompanySmsInfo?.balanceCheck && (
              <div style={{ fontWeight: 'bold' }}>
                {t('minCreditAlert', {
                  number: currentCompanySmsInfo?.allowBalance,
                })}
              </div>
            )}
            <Input
              id={`notificationLine-${currentCompanySmsInfo?.id}`}
              type="text"
              label={t('notificationLine') as string}
              value={(notificationLine ?? '') as string}
              onChange={(e) => setNotificationLine(e.target.value)}
            />
            {selectedUserNotificationLine && (
              <div>
                <span
                  className="inline-block px-3 py-1 text-white"
                  style={{ background: black, borderRadius: '0.5rem' }}
                >
                  {selectedUserNotificationLine}
                </span>
              </div>
            )}
            <div className="flex-1 flex justify-end">
              <Button
                type={'button'}
                onClick={handleSaveUserInfo}
                color={'black-outline'}
                loading={loading}
              >
                {t('save')}
              </Button>
            </div>
          </CheckCreditsItem>
        </CheckCreditsContainer>
      </Box>
      <Box>
        <DateContainer>
          <ButtonGroup
            items={dateOptions}
            selected={selectedDateOption}
            onSelect={(itemKey) => {
              if (selectedDateOption) {
                setCustomDate(undefined);
              }
              setSelectedDateOptions(itemKey as keyof typeof dateOptions);
            }}
            style={{ width: '100%', margin: '0 auto' }}
          />
          {selectedDateOption === 'custom' && (
            <Box>
              <MultiDatePicker_Range
                isEditable={true}
                hasTime={false}
                label={t('customTime')}
                values={{
                  startDate: customDate?.startDate as number,
                  dueDate: customDate?.dueDate as number,
                }}
                setDate={(values) => {
                  const startDate = values.startDate;
                  const dueDate = values.dueDate;
                  setCustomDate({
                    startDate: startDate as number,
                    dueDate: dueDate as number,
                  });
                }}
              />
            </Box>
          )}
        </DateContainer>

        <div className="my-5">
          <h4 className="font-bold py-2">{t('smsStats')}</h4>
          <table>
            <thead>
              <tr>
                <th>{t('topic') as string}</th>
                <th>{t('smsNumber') as string}</th>
                <th>{t('avgSmsLength') as string}</th>
                <th>{t('sumCost') as string}</th>
              </tr>
            </thead>
            <tbody>
              {clientStats?.map((stat, ind) => (
                <tr key={`${stat.sourceType}-${ind}`}>
                  <td>{t(`${stat.sourceType}`)}</td>
                  <td>{stat.requestCount}</td>

                  <td>
                    {(
                      stat.totalMessageLength / stat.requestCount
                    ).toLocaleString(undefined, { maximumFractionDigits: 1 })}
                  </td>
                  <td>{stat.totalCost.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Box>
      <Box>
        <h4 className="font-bold py-2">{t('chargeHistory')}</h4>
        <table>
          <thead>
            <tr>
              <th>{t('date') as string}</th>
              <th>
                {t('chargeAmount') as string}({t('IRR') as string})
              </th>
              <th>{t('description') as string}</th>
            </tr>
          </thead>
          <tbody>
            {history?.map((item) => {
              return (
                <tr key={item.id}>
                  <td>
                    {smartDateString(new Date(item.date), true, true, true)}
                  </td>
                  <td>{item.amountIRR.toLocaleString()}</td>
                  <td>{item.description}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>
    </Container>
  );
};

export default AdminSmsPanel;
